import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { GAME_WIDTH } from "../constants";
import type { MapModule, ObstacleData } from "../types";
import type { StoryBiomeId } from "../story/storyConfig";

interface RngLike {
  next(): number;
  range(min: number, max: number): number;
  pick<T>(items: T[]): T;
}

export class LandmarkPlacementSystem {
  static decorateModule(module: MapModule, rng: RngLike) {
    if (!module.biome) return;
    const pathY = module.y + module.height * rng.range(0.28, 0.72);
    const pathX = this.samplePath(module.safePath, pathY);
    const side = rng.pick([-1, 1]);
    const x = Phaser.Math.Clamp(pathX + side * rng.range(128, 228), 42, GAME_WIDTH - 42);
    const idBase = `${module.id}-story-${module.biome}`;

    const add = (key: string, dx: number, dy: number, width: number, height: number, rotation: number, ySort = false) => {
      module.obstacles.push({
        id: `${idBase}-${module.obstacles.length}`,
        key,
        x: Phaser.Math.Clamp(x + dx, 24, GAME_WIDTH - 24),
        y: pathY + dy,
        width,
        height,
        rotation,
        blocking: false,
        decorative: true,
        ySort,
        story: true
      });
    };

    this.addBiomeLandmark(module.biome, add, rng);
  }

  private static addBiomeLandmark(
    biome: StoryBiomeId,
    add: (key: string, dx: number, dy: number, width: number, height: number, rotation: number, ySort?: boolean) => void,
    rng: RngLike
  ) {
    if (biome === "dark_forest_entry") {
      add(AssetKeys.obstacle.rootWall, 0, 0, 150, 112, rng.range(-0.3, 0.3), true);
      add(AssetKeys.deco.mark, rng.range(-38, 38), 36, 76, 58, rng.range(-0.8, 0.8));
      return;
    }
    if (biome === "bright_forest_breathing_area" || biome === "pack_call_exit") {
      add(AssetKeys.deco.moss, 0, 0, 150, 104, rng.range(-0.45, 0.45));
      add(AssetKeys.deco.grass, rng.range(-54, 54), rng.range(-42, 42), 112, 96, rng.range(-0.6, 0.6));
      return;
    }
    if (biome === "ancient_ruins") {
      add(AssetKeys.obstacle.ruin, 0, 0, 178, 142, rng.range(-0.24, 0.24), true);
      add(AssetKeys.obstacle.stump, rng.range(-48, 48), 54, 72, 68, rng.range(-1.0, 1.0), true);
      add(AssetKeys.deco.cracks, rng.range(-28, 28), 18, 106, 78, rng.range(-0.8, 0.8));
      return;
    }
    if (biome === "dense_forest_corridor") {
      add(AssetKeys.obstacle.treeTwisted, 0, 0, 160, 196, rng.range(-0.16, 0.16), true);
      add(AssetKeys.obstacle.rootCurve, rng.range(-34, 34), 68, 132, 88, rng.range(-0.5, 0.5), true);
      return;
    }
    if (biome === "corrupted_pressure_zone" || biome === "final_spirit_chase") {
      add(AssetKeys.deco.cracks, 0, 0, 136, 98, rng.range(-0.7, 0.7));
      add(AssetKeys.deco.puddle, rng.range(-42, 42), 42, 118, 72, rng.range(-0.7, 0.7));
      add(AssetKeys.deco.mushrooms, rng.range(-58, 58), -34, 56, 56, rng.range(-0.4, 0.4));
      return;
    }
    if (biome === "rocky_open_area") {
      add(AssetKeys.obstacle.rockHuge, 0, 0, 156, 126, rng.range(-0.35, 0.35), true);
      add(AssetKeys.obstacle.rockCluster, rng.range(-50, 50), 42, 88, 78, rng.range(-0.55, 0.55), true);
    }
  }

  private static samplePath(points: Array<{ x: number; y: number }>, y: number) {
    for (let i = 0; i < points.length - 1; i += 1) {
      const a = points[i];
      const b = points[i + 1];
      if (y >= a.y && y <= b.y) {
        const t = (y - a.y) / (b.y - a.y);
        return a.x + (b.x - a.x) * t;
      }
    }
    return points[points.length - 1].x;
  }
}
